import { FormField, SpaceBetween, TokenGroup } from '@amzn/awsui-components-core';
import type { Employee } from '@amzn/stencil-react-components/employee-lookup';
import { EmployeeLookup } from '@amzn/stencil-react-components/employee-lookup';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { searchEmployeesFromFintechPapi, getEmployeeByLogin } from 'src/api/papi-service';
import { FintechPapiEmployee } from 'src/api/interfaces';
import { logger } from 'src/logger';
import type { SelectedEmployee } from './SingleEmployeeLookup';

const SEARCH_DEBOUNCE_DELAY = 300;

interface MultiEmployeeLookupProps {
  label?: React.ReactNode;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  value: SelectedEmployee[];
  onChange: (employees: SelectedEmployee[]) => void;
  errorText?: string;
}

const mapPapiEmployeeToResult = (emp: FintechPapiEmployee): Employee => ({
  username: emp.basicInfo.login?.toLowerCase() ?? '',
  fullName: `${emp.basicInfo.firstName ?? ''} ${emp.basicInfo.lastName ?? ''}`.trim(),
  businessTitle: emp.basicInfo.businessTitle,
  city: emp.workLocation?.city || '',
  country: emp.workLocation?.country || '',
  departmentName: emp.basicInfo.costCenterName
});

const MultiEmployeeLookup: React.FC<MultiEmployeeLookupProps> = ({
  label = 'Other Contacts',
  description,
  placeholder = 'Search by name',
  disabled = false,
  value,
  onChange,
  errorText
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string>();
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lookupKey, setLookupKey] = useState(0);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const handleQueryChange = useCallback((newQuery: string) => {
    setQuery(newQuery);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (!newQuery || newQuery.length < 2) {
      setSearchResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const response = await searchEmployeesFromFintechPapi(newQuery, 10);
        setSearchResults((response.employees ?? []).map(mapPapiEmployeeToResult));
      } catch (error) {
        logger.error('Employee search error', { error });
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    }, SEARCH_DEBOUNCE_DELAY);
  }, []);

  const handleSelection = useCallback(
    async (selected: Employee | Employee[] | undefined) => {
      if (!selected || Array.isArray(selected)) return;

      const login = selected.username?.toLowerCase();
      if (!login) return;

      if (value.some((u) => u.login.toLowerCase() === login)) {
        setValidationError('This user has already been added');
        return;
      }

      setIsValidating(true);
      setValidationError(undefined);

      try {
        const response = await getEmployeeByLogin(login);
        if (!response.employee) {
          setValidationError('User not found in directory');
          return;
        }

        const emp = response.employee;
        if (emp.basicInfo.jobStatus === 'INACTIVE' || emp.basicInfo.employeeStatus === 'INACTIVE') {
          setValidationError('User is inactive and cannot be added');
          return;
        }

        const fullName = `${emp.basicInfo.firstName ?? ''} ${emp.basicInfo.lastName ?? ''}`.trim();
        const newEmployee: SelectedEmployee = { login: emp.basicInfo.login?.toLowerCase() ?? login, fullName: fullName || login };
        onChange([...value, newEmployee]);
        setValidationError(undefined);
        setQuery('');
        setSearchResults([]);
        setLookupKey((prev) => prev + 1);
      } catch (error) {
        logger.error('Employee validation error', { error });
        setValidationError('Validation failed. Please try again.');
      } finally {
        setIsValidating(false);
      }
    },
    [value, onChange]
  );

  const handleRemove = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange]
  );

  const tokenItems = value.map((user) => ({
    label: `${user.fullName} (${user.login})`,
    dismissLabel: `Remove ${user.fullName}`
  }));

  const displayError = errorText || validationError;

  return (
    <FormField label={label} description={description} errorText={displayError}>
      <SpaceBetween size="xs">
        <EmployeeLookup
          key={lookupKey}
          query={query}
          onQueryChange={handleQueryChange}
          results={searchResults}
          isLoading={isLoading}
          onSelectionChange={handleSelection}
          placeholder={placeholder}
          disabled={disabled || isValidating}
          error={!!displayError}
        />
        {value.length > 0 && <TokenGroup items={tokenItems} onDismiss={({ detail: { itemIndex } }) => handleRemove(itemIndex)} />}
      </SpaceBetween>
    </FormField>
  );
};

export default MultiEmployeeLookup;
